import { useState } from 'react'
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Info,
  Shield,
  Wallet,
} from 'lucide-react'
import {
  BOOKING_SERVICE_FEE,
  calculatePaymentBreakdown,
  formatRupee,
  isPartialPayment,
  PAYMENT_METHOD_ONLINE,
  PAYMENT_METHOD_PARTIAL,
  PARTIAL_PAYMENT_PERCENT,
  type PaymentBreakdown,
} from '../../../../utils/paymentPricing'

interface PaymentStepProps {
  paymentMethod: string
  paymentMethods: string[]
  basePrice: number
  nights: number
  roomTotal: number
  error?: string
  onChange: (value: string) => void
}

type PaymentTheme = 'green' | 'orange'

const themeStyles: Record<
  PaymentTheme,
  {
    tabActive: string
    tabRadio: string
    cardSelected: string
    cardIdle: string
    badge: string
    title: string
    summaryBg: string
    summaryAmount: string
    highlightRow: string
    footer: string
    footerAmount: string
  }
> = {
  green: {
    tabActive: 'border-green-500 bg-green-50 text-green-900',
    tabRadio: 'border-green-500 bg-green-500',
    cardSelected: 'border-green-500 ring-4 ring-green-100 shadow-md',
    cardIdle: 'border-gray-200 hover:border-green-200',
    badge: 'bg-green-100 text-green-700',
    title: 'text-green-700',
    summaryBg: 'bg-green-50 border-green-100',
    summaryAmount: 'text-green-700',
    highlightRow: 'bg-green-50 text-green-800 border-green-200',
    footer: 'bg-green-50 border-green-200',
    footerAmount: 'text-green-700',
  },
  orange: {
    tabActive: 'border-orange-500 bg-orange-50 text-orange-900',
    tabRadio: 'border-orange-500 bg-orange-500',
    cardSelected: 'border-orange-500 ring-4 ring-orange-100 shadow-md',
    cardIdle: 'border-gray-200 hover:border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    title: 'text-orange-700',
    summaryBg: 'bg-orange-50 border-orange-100',
    summaryAmount: 'text-orange-700',
    highlightRow: 'bg-orange-50 text-orange-800 border-orange-200',
    footer: 'bg-orange-50 border-orange-200',
    footerAmount: 'text-orange-700',
  },
}

const getMethodMeta = (method: string) => {
  if (isPartialPayment(method)) {
    return {
      theme: 'orange' as PaymentTheme,
      tabLabel: `Pay ${Math.round(PARTIAL_PAYMENT_PERCENT * 100)}% Now (Partial Payment)`,
      title: 'Partial Payment (20%)',
      selectedTitle: 'Partial Payment (20%) Selected',
      description: 'Pay 20% deposit plus service charge now; remaining balance at check-in.',
      payNowHint: '20% deposit + service charge',
      dueHint: 'Remaining room balance',
    }
  }

  return {
    theme: 'green' as PaymentTheme,
    tabLabel: 'Pay Full Amount',
    title: 'Full Payment',
    selectedTitle: 'Full Payment Selected',
    description: 'Pay the entire amount now and enjoy a hassle-free stay.',
    payNowHint: 'Room charges only — service fee waived',
    dueHint: 'Nothing to pay later',
  }
}

interface BreakdownRow {
  label: string
  amount: number
  muted?: boolean
  highlight?: boolean
  divider?: boolean
  discounted?: boolean
}

const buildBreakdownRows = (
  breakdown: PaymentBreakdown,
  basePrice: number,
  nights: number,
  isPartial: boolean
): BreakdownRow[] => {
  const roomLabel =
    nights > 0
      ? `Room Charges (${formatRupee(basePrice)} × ${nights} night${nights === 1 ? '' : 's'})`
      : 'Room Charges'

  if (!isPartial) {
    return [
      { label: roomLabel, amount: breakdown.roomTotal },
      { label: 'Service Charge', amount: breakdown.serviceFee, discounted: true },
      { label: 'Pay Now (Online)', amount: breakdown.payNow, highlight: true, divider: true },
      { label: 'Due at Check-in', amount: breakdown.dueAtCheckin, muted: true },
    ]
  }

  return [
    { label: roomLabel, amount: breakdown.roomTotal },
    { label: 'Advance Payment (20%)', amount: breakdown.partialDeposit },
    { label: 'Service Charge', amount: breakdown.serviceFee },
    { label: 'Pay Now (Online)', amount: breakdown.payNow, highlight: true, divider: true },
    { label: 'Remaining Room Balance', amount: breakdown.remainingRoomBalance },
    { label: 'Due at Check-in', amount: breakdown.dueAtCheckin, highlight: true, divider: true },
  ]
}

interface PaymentOptionCardProps {
  method: string
  selected: boolean
  recommended?: boolean
  basePrice: number
  nights: number
  roomTotal: number
  showDetails: boolean
  onToggleDetails: () => void
  onSelect: () => void
}

const PaymentOptionCard = ({
  method,
  selected,
  recommended,
  basePrice,
  nights,
  roomTotal,
  showDetails,
  onToggleDetails,
  onSelect,
}: PaymentOptionCardProps) => {
  const meta = getMethodMeta(method)
  const theme = themeStyles[meta.theme]
  const breakdown = calculatePaymentBreakdown(roomTotal, method)
  const rows = buildBreakdownRows(breakdown, basePrice, nights, breakdown.isPartial)

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-2xl border-2 p-4 sm:p-5 transition-all duration-200 ${
        selected ? theme.cardSelected : theme.cardIdle
      }`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
            selected ? theme.tabRadio : 'border-gray-300 bg-white'
          }`}
        >
          {selected && <div className="w-2 h-2 rounded-full bg-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {selected ? (
              <CheckCircle2 className={`h-5 w-5 ${meta.theme === 'green' ? 'text-green-600' : 'text-orange-600'}`} />
            ) : null}
            <span className={`text-sm sm:text-base font-bold uppercase tracking-wide ${theme.title}`}>
              {selected ? meta.selectedTitle : meta.title}
            </span>
            {recommended && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${theme.badge}`}>
                Recommended
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">{meta.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className={`rounded-xl border p-3 ${theme.summaryBg}`}>
          <div className="flex items-center gap-1.5 text-gray-600 mb-1">
            <Wallet className="h-4 w-4" />
            <span className="text-xs font-medium">Pay Now</span>
          </div>
          <p className={`text-xl sm:text-2xl font-bold ${theme.summaryAmount}`}>
            {formatRupee(breakdown.payNow)}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{meta.payNowHint}</p>
        </div>
        <div className={`rounded-xl border p-3 ${theme.summaryBg}`}>
          <div className="flex items-center gap-1.5 text-gray-600 mb-1">
            <Building2 className="h-4 w-4" />
            <span className="text-xs font-medium">Due at Check-in</span>
          </div>
          <p className={`text-xl sm:text-2xl font-bold ${theme.summaryAmount}`}>
            {formatRupee(breakdown.dueAtCheckin)}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{meta.dueHint}</p>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-900">Payment Breakdown</span>
          <span
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation()
              onToggleDetails()
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                onToggleDetails()
              }
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {showDetails ? 'Hide details' : 'Show details'}
            {showDetails ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
        </div>

        {showDetails && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto] gap-x-4 px-3 py-2 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <span>Description</span>
              <span>Amount</span>
            </div>
            <div className="divide-y divide-gray-100">
              {rows.map((row) => (
                <div key={row.label}>
                  {row.divider && <div className="border-t border-dashed border-gray-300" />}
                  <div
                    className={`grid grid-cols-[1fr_auto] gap-x-4 px-3 py-2.5 text-sm ${
                      row.highlight ? `font-bold border-y border-dashed ${theme.highlightRow}` : ''
                    } ${row.muted ? 'text-gray-500' : 'text-gray-800'}`}
                  >
                    <span className="flex items-center gap-1">
                      {row.label}
                      {row.label === 'Service Charge' && !row.highlight && (
                        <Info className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </span>
                    <span className="font-medium whitespace-nowrap">
                      {row.discounted ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="line-through text-gray-400">{formatRupee(row.amount)}</span>
                          <span className="text-green-600 font-semibold">Free</span>
                        </span>
                      ) : (
                        formatRupee(row.amount)
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`rounded-xl border p-3 sm:p-4 ${theme.footer}`}>
        <div className="flex items-center gap-2 text-gray-700">
          <Shield className={`h-4 w-4 ${meta.theme === 'green' ? 'text-green-600' : 'text-orange-600'}`} />
          <span className="text-sm font-semibold">Total Booking Cost</span>
        </div>
        <p className={`text-2xl sm:text-3xl font-bold mt-1 ${theme.footerAmount}`}>
          {formatRupee(breakdown.totalCustomerPays)}
        </p>
        <p className="text-[11px] sm:text-xs text-gray-500 mt-1">
          {breakdown.isPartial
            ? 'Service charge is included in the online payment now.'
            : `Service charge of ${formatRupee(breakdown.serviceFeeDiscount)} waived on full payment.`}
        </p>
      </div>
    </button>
  )
}

const PaymentStep = ({
  paymentMethod,
  paymentMethods,
  basePrice,
  nights,
  roomTotal,
  error,
  onChange,
}: PaymentStepProps) => {
  const [showFullDetails, setShowFullDetails] = useState(true)
  const [showPartialDetails, setShowPartialDetails] = useState(true)

  const fullMethod =
    paymentMethods.find((method) => !isPartialPayment(method)) ?? PAYMENT_METHOD_ONLINE
  const partialMethod =
    paymentMethods.find((method) => isPartialPayment(method)) ?? PAYMENT_METHOD_PARTIAL

  const renderTab = (method: string, recommended?: boolean) => {
    const selected = paymentMethod === method
    const meta = getMethodMeta(method)
    const theme = themeStyles[meta.theme]

    return (
      <label
        key={method}
        className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 cursor-pointer transition-all ${
          selected ? theme.tabActive : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <input
          type="radio"
          name="paymentMethod"
          value={method}
          checked={selected}
          onChange={(event) => onChange(event.target.value)}
          className="sr-only"
          required
        />
        <div
          className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
            selected ? theme.tabRadio : 'border-gray-300 bg-white'
          }`}
        >
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
        </div>
        <span className="text-xs sm:text-sm font-semibold text-gray-900">{meta.tabLabel}</span>
        {recommended && (
          <span className={`hidden sm:inline text-[10px] font-semibold px-2 py-0.5 rounded-full ${theme.badge}`}>
            Recommended
          </span>
        )}
      </label>
    )
  }

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Choose How You Want to Pay</h3>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">Select your preferred payment option</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-red-700 text-sm font-semibold flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Payment failed
          </p>
          <p className="text-red-600 text-xs sm:text-sm mt-1">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {renderTab(fullMethod, true)}
        {renderTab(partialMethod)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PaymentOptionCard
          method={fullMethod}
          selected={paymentMethod === fullMethod}
          recommended
          basePrice={basePrice}
          nights={nights}
          roomTotal={roomTotal}
          showDetails={showFullDetails}
          onToggleDetails={() => setShowFullDetails((value) => !value)}
          onSelect={() => onChange(fullMethod)}
        />
        <PaymentOptionCard
          method={partialMethod}
          selected={paymentMethod === partialMethod}
          basePrice={basePrice}
          nights={nights}
          roomTotal={roomTotal}
          showDetails={showPartialDetails}
          onToggleDetails={() => setShowPartialDetails((value) => !value)}
          onSelect={() => onChange(partialMethod)}
        />
      </div>

      <div className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
        <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-xs sm:text-sm text-blue-900 leading-relaxed">
          <span className="font-semibold">Good to know:</span> Full payment waives the {formatRupee(BOOKING_SERVICE_FEE)}{' '}
          service charge. Partial payment includes the service charge in the online deposit.
        </p>
      </div>
    </div>
  )
}

export default PaymentStep
